import { computed, inject, Injectable } from '@angular/core';
import {
  auditTime,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  merge,
  Observable,
  of,
  skip,
  startWith,
} from 'rxjs';
import { isLoggerNotAssignedToPipeNode } from '@app/core/methods/logger-pipe-assignment';
import { IMapOverlay } from '@app/api/interfaces/map-overlay.interface';
import { IAdapterMapConfig } from '@app/main/main-content/map/interfaces/adapter-map-config.interface';
import { ILoggerMarker, ILoggerMarkerAction } from '@app/main/main-content/map/interfaces/logger-marker.interface';
import { filter, shareReplay, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { LoggerMarkerAction } from '@app/main/main-content/map/enums/logger-marker-action.enum';
import { IEventMarker, IEventMarkerAction } from '@app/main/main-content/map/interfaces/event-marker.interface';
import { IEvent } from '@app/api/interfaces/event.interface';
import {
  ICorrelationMarker,
  ICorrelationMarkerAction,
} from '@app/main/main-content/map/interfaces/correlation-marker.interface';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { CorrelationMarkerAction } from '@app/main/main-content/map/enums/correlation-marker-action.enum';
import { EventMarkerAction } from '@app/main/main-content/map/enums/event-marker-action.enum';
import {
  IMapCorrelationPath,
  IMapCorrelationPathAction,
  IMapCorrelationPathSelectedState,
} from '@app/main/main-content/map/interfaces/map-correlation-path.interface';
import { selectPipesSource } from '@app/main/main-screen/store/data/pipes/pipes.selectors';
import { selectMainProjectViewMeasurementSystem } from '@app/main/main-screen/store/projects/projects.selectors';
import { IMapPipe } from '@app/main/main-content/map/layers/pipes/merge/interfaces/map-pipe.interface';
import { MeasurementSystem } from '@app/core/enums/measurement-system.enum';
import { selectAllMapOverlays } from '@app/main/main-screen/store/data/map-overlays/map-overlays.selectors';
import { ZoomToFitType } from '@app/main/main-content/map/interfaces/zoom-to-fit.interface';
import { DoToolbarService } from '@app/main/main-content/display-options/services/do-toolbar/do-toolbar.service';
import { DoFilterKey } from '@app/main/main-content/display-options/enums/do-filter-key.enum';
import {
  isCorrelationVisible,
  isEventVisible,
  isLoggerVisible,
} from '@app/main/leak-detection/utils/ld-filter-visibility';
import { LdLoggerFiltersService } from '@app/main/leak-detection/services/ld-logger-filters/ld-logger-filters.service';
import { IPipePolyline, IPipePolylineAction } from '@app/main/main-content/map/interfaces/pipe-polyline.interface';
import { PipePolylineAction } from '@app/main/main-content/map/enums/pipe-polyline-action.enum';
import { MapStylesConfigService } from '@app/main/main-content/map/services/map-styles-config.service';
import { ICorrelationPath } from '@app/api/interfaces/correlation-path.interface';
import { CorrelationPathAction } from '@app/main/main-content/map/enums/correlation-path-action.enum';
import { selectLoggerDeploymentEntities } from '@app/main/main-screen/store/data/logger-deployments/logger-deployments.selectors';
import { LeakDetectionStatus } from '@app/core/enums/leak-detection-status.enum';
import { selectAllEvents } from '@app/main/main-screen/store/data/events/events.selectors';
import { MarkerToastService } from '@app/main/main-content/map/services/marker-toast.service';
import { selectMainDataIsLoading } from '@app/main/main-screen/store/main-data/main-data.selectors';
import { CorrelationPathsService } from '@app/main/leak-detection/services/correlation-paths/correlation-paths.service';
import { MapOverlayLoaderService } from '@app/main/main-content/map/layers/overlays/services/map-overlay-loader.service';
import { formatSerialNo } from '@app/shared/utils/string';
import { mapObjectBy } from '@app/shared/utils/array-mappers';
import { Nullable } from 'primeng/ts-helpers';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { selectProjectEntities } from '@app/main/main-screen/store/projects/projects.selectors';
import { LeakDetectionMode } from '@app/core/enums/leak-detection-mode.enum';
import { getLoggerMeasurementPrediction } from '@app/main/leak-detection/utils/logger-measurement-prediction';
import { getAngleBetweenPoints } from '@app/main/main-content/map/utils/geometry';
import { CorrelationType } from '@app/core/enums/correlation-type.enum';
import {
  LdActionPlace,
  LdActionType,
  LdCoordinatorActionShowInStreetView,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.actions';
import { LdCoordinatorStore } from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.store';
import { LdTabs } from '@app/main/leak-detection/enums/ld-tabs';
import { ColorGroup } from '@app/core/enums/color-group.enum';
import { ColorManagementService } from '@app/core/services/colors/color-management.service';
import { CommonStateStatus } from '@app/core/enums/common-state-status.enum';
import { IMapBounds } from '@app/main/main-content/map/interfaces/map-bounds.interface';
import { IMapRightClick } from '@app/main/main-content/map/interfaces/map-right-click.interface';
import { LdDataTimedService } from '@app/main/leak-detection/services/leak-detection-data-timed/leak-detection-data-timed.service';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { IPosition } from '@app/core/interfaces/position.interface';
import {
  deriveLdEventInvestigationResult,
  deriveLdEventRepairStatus,
} from '@app/main/leak-detection/utils/ld-event-status';
import { IMapClick } from '@app/main/main-content/map/interfaces/map-click.interface';
import { isPointInBounds } from '@app/main/main-content/map/utils/point-in-bounds';
import { PositionAdapter } from '@app/main/main-content/map/classes/position-adapter';
import { ContextMenuService } from '@app/main/main-content/context-menu/services/context-menu.service';
import { LeakDetectionEventPositionService } from '@app/main/leak-detection/services/leak-detection-event-position/leak-detection-event-position.service';
import { LdContextMenuEventPositionService } from '@app/main/leak-detection/context-menus/services/ld-context-menu-event-position.service';
import { MainMapDataAdapter } from '@app/main/main-screen/services/main-map-data/main-map-data.adapter';
import { MAP_POSITION_PICK_CURSOR } from '@app/main/main-content/map/configs/map-cursor.config';

type ILdCorrelationPathsFocus = { type: 'zoomToFit' } | { type: 'pin'; position: IPosition } | { type: 'none' };

@Injectable({ providedIn: 'root' })
export class LeakDetectionMapService extends MainMapDataAdapter {
  private doToolbarService = inject(DoToolbarService);
  private mapStylesConfig = inject(MapStylesConfigService);
  private correlationPathsService = inject(CorrelationPathsService);
  private mapOverlayLoaderService = inject(MapOverlayLoaderService);
  private ldCoordinatorStore = inject(LdCoordinatorStore);
  private colorManagementService = inject(ColorManagementService);
  private ldDataTimedService = inject(LdDataTimedService);
  private ldLoggerFiltersService = inject(LdLoggerFiltersService);
  private markerToastService = inject(MarkerToastService);
  private contextMenuService = inject(ContextMenuService);
  private eventPosition = inject(LeakDetectionEventPositionService);
  private ldContextMenuEventPosition = inject(LdContextMenuEventPositionService);

  private readonly eventPositionEvent$ = toObservable(this.eventPosition.event);

  /** the event being moved shows at the spot picked for it, not where it is saved */
  private readonly eventPositionOverride$ = toObservable(
    computed(() => ({ eventId: this.eventPosition.event()?.id, position: this.eventPosition.position() }))
  );

  private readonly activeTabS = toSignal(this.ldCoordinatorStore.activeTab$);
  private readonly cellIdModeEnabled = toSignal(this.doToolbarService.cellIdModeEnabled, { initialValue: false });

  private _selectedPipes = new BehaviorSubject<IPipePolyline<IMapPipe>[]>([]);
  public selectedPipes$ = this._selectedPipes.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: true }));
  private setSelectedPipes(selectedPipes: IPipePolyline<IMapPipe>[]): void {
    this._selectedPipes.next(selectedPipes);
  }
  private selectedPipesMapS = toSignal(this.selectedPipes$.pipe(map(pipes => mapObjectBy(pipes, item => item.id))), {
    initialValue: {} as Record<string, IPipePolyline<IMapPipe>>,
  });

  private _selectedCorrelationPathStates = new BehaviorSubject<IMapCorrelationPathSelectedState[]>([]);
  public selectedCorrelationPathStates$ = this._selectedCorrelationPathStates
    .asObservable()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  private setSelectedCorrelationPathStates(correlationPathStates: IMapCorrelationPathSelectedState[]): void {
    this._selectedCorrelationPathStates.next(correlationPathStates);
  }
  private selectedCorrelationPathStatesS = toSignal(this.selectedCorrelationPathStates$, { initialValue: [] });

  // The focus intent travels together with the paths so that whoever consumes the
  // emission knows how the map should react (zoom to fit for correlations, pin for loggers).
  private _correlationPathsView = new BehaviorSubject<{
    paths: IMapCorrelationPath[];
    focus: ILdCorrelationPathsFocus;
  }>({
    paths: [],
    focus: { type: 'none' },
  });
  public correlationPaths$ = this._correlationPathsView.pipe(
    map(({ paths }) => paths),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  private setCorrelationPathsView(
    paths: IMapCorrelationPath[],
    focus: ILdCorrelationPathsFocus = { type: 'zoomToFit' }
  ): void {
    this.setSelectedCorrelationPathStates([]);
    this._correlationPathsView.next({ paths, focus });
  }
  private correlationPathsS = toSignal(this.correlationPaths$, { initialValue: [] });

  private readonly loggerMarkers$ = combineLatest([
    this.ldDataTimedService.ldLoggers$.pipe(
      withLatestFrom(this.ldDataTimedService.ldLoggersMapByDeploymentId$),
      map(([loggers, loggersMapByDeploymentId]) => {
        return loggers.filter(logger => {
          const ldLoggers = loggersMapByDeploymentId[logger.deployment.id];

          return ldLoggers.length > 1 ? this.ldDataTimedService.getActualLdLogger(ldLoggers)?.id === logger.id : true;
        });
      })
    ),
    this.ldLoggerFiltersService.getLoggerFilters(),
  ]).pipe(
    withLatestFrom(
      this.ldDataTimedService.ldLoggersMapByDeploymentId$,
      this.store.select(selectMainProjectId),
      this.store.select(selectProjectEntities)
    ),
    map(([[loggers, loggerFilters], loggersMapByDeploymentId, projectId, projectsMap]) => {
      const leakDetectionMode = projectsMap[projectId!]?.leakDetectionMode ?? LeakDetectionMode.LeakScore;

      return loggers.reduce((acc, logger) => {
        if (isLoggerVisible(loggerFilters, logger)) {
          const prediction = getLoggerMeasurementPrediction(logger.measurement, leakDetectionMode);

          acc.push({
            id: logger.id,
            type: logger.deployment.loggerType,
            status: prediction.status ?? LeakDetectionStatus.Neutral,
            position: logger.deployment.coordinates,
            line:
              logger.deployment.adjustedCoordinates &&
              (logger.deployment.coordinates.lat !== logger.deployment.adjustedCoordinates.lat ||
                logger.deployment.coordinates.lon !== logger.deployment.adjustedCoordinates.lon)
                ? {
                    path: [logger.deployment.coordinates, logger.deployment.adjustedCoordinates],
                  }
                : undefined,
            info: {
              serial: formatSerialNo(logger.deployment.serialNo),
              metricValue: prediction.value != null ? String(prediction.value) : undefined,
              noiseDbd:
                logger.measurement?.lowestNoiseDecibel != null
                  ? logger.measurement.lowestNoiseDecibel.toFixed(1)
                  : undefined,
              aiPredictor: prediction.value != null ? String(prediction.value) : undefined,
              assetIdentifier: logger.deployment?.assetId ?? undefined,
              cellIdNumber: logger.deployment.servingCellId ?? undefined,
            },
            config: {
              showTooltip: true,
              cellIdColor: this.getCellIdColor(logger.deployment.servingCellId),
              isPositionTemporary: logger.deployment.isPositionTemporary,
              isUnassigned: isLoggerNotAssignedToPipeNode(logger.deployment.adjustedCoordinates),
            },
            raw: logger,
            selected$: computed(() => {
              const cellIdModeEnabled = this.cellIdModeEnabled();

              if (cellIdModeEnabled) {
                if (this.ldCoordinatorStore.cellIdSelectionFromMapS()) return false;
                return logger.deployment.servingCellId
                  ? !!this.ldCoordinatorStore.selectedCellsIdsMapS()[logger.deployment.servingCellId]
                  : false;
              }

              if (this.activeTabS() === LdTabs.LoggerNoise) {
                const relatedLdLoggers = loggersMapByDeploymentId[logger.deployment.id];

                const selectedLoggersMapS = this.ldCoordinatorStore.selectedLoggersMapS();

                return relatedLdLoggers.some(relatedLdLogger => !!selectedLoggersMapS[relatedLdLogger.id]);
              }
              return false;
            }),
            isCellIdVisible$: computed(() => {
              if (!this.cellIdModeEnabled()) return false;

              const ids = this.ldCoordinatorStore.visibleCellsIdsMapS();
              if (!ids) return true;

              return logger.deployment.servingCellId ? !!ids[logger.deployment.servingCellId] : false;
            }),
            correlationPath$: computed(() => {
              const relatedCorrelationPaths = this.correlationPathsS().filter(correlationPath =>
                correlationPath.loggers?.find(corrPathLogger => corrPathLogger.id === logger.id)
              );

              return relatedCorrelationPaths.length ? relatedCorrelationPaths[0] : null;
            }),
          });
        }

        return acc;
      }, [] as ILoggerMarker[]);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public override getMapConfig(): IAdapterMapConfig {
    return {
      ...super.getMapConfig(),
      loggerMarkerOptimization: true,
      loggerMarkerMinZoom: 10,
      correlationMarkerOptimization: true,
      correlationMarkerMinZoom: 10,
      correlationPathOptimization: true,
      correlationPathMinZoom: 10,
      eventMarkerOptimization: true,
      eventMarkerMinZoom: 10,
      zonePolygonMinZoom: 10,
      mapOverlayMinZoom: 12,
      toolbarNativeSearch: true,
      streetViewMarkerOverlay: true,
      streetViewNearbyMarkerDistanceMeters: 100,
    };
  }

  public getMapHandlers() {
    return merge(
      this.getNewDataLoadedHandler(),
      this.getNewDataLoadingHandler(),
      this.getProjectChangedHandler(),
      this.getCorrelationPathLoadingHandler(),
      this.getMapTargetChangedHandler(),
      this.getToolbarZoomToFitHandler(),
      this.getCoordinatorActionZoomToFitItemHandler(),
      this.getCoordinatorActionShowInStreetViewHandler(),
      this.getCoordinatorSelectionChangedHandler(),
      this.getLoggerMarkerActionHandler(),
      this.getCorrelationMarkerActionHandler(),
      this.getCorrelationPathActionHandler(),
      this.getEventMarkerActionHandler(),
      this.getPipePolylineActionHandler(),
      this.getMapBoundaryActionHandler(),
      this.getMapRightClickHandler(),
      this.getMapClickHandler(),
      this.getEventPositionModeHandler()
    );
  }

  public override getLoggerMarkers(): Observable<ILoggerMarker<ILdLogger>[]> {
    return this.loggerMarkers$;
  }

  public override getCorrelationMarkers(): Observable<ICorrelationMarker[]> {
    return combineLatest([
      this.ldDataTimedService.correlations$,
      this.doFiltersService.getFilter(DoFilterKey.Correlation),
      this.store.select(selectLoggerDeploymentEntities),
    ]).pipe(
      map(([correlations, correlationFilter, loggerDeploymentsMap]) =>
        correlations
          .filter(correlation => isCorrelationVisible(correlationFilter, correlation))
          .map(correlation => {
            return {
              id: correlation.id,
              type: correlation.correlationType,
              status: correlation.suppressed ? CommonStateStatus.Normal : correlation.qualityStatus,
              position: correlation.coordinates,
              rotationAngle:
                correlation.correlationType !== CorrelationType.Pinpoint
                  ? getAngleBetweenPoints(
                      loggerDeploymentsMap[correlation.logger1DeploymentId]?.coordinates,
                      loggerDeploymentsMap[correlation.logger2DeploymentId]?.coordinates
                    )
                  : undefined,
              config: {
                showTooltip: true,
              },
              info: {
                quality: correlation.quality,
                logger1SerialNo: formatSerialNo(loggerDeploymentsMap[correlation.logger1DeploymentId]?.serialNo),
                logger2SerialNo: formatSerialNo(loggerDeploymentsMap[correlation.logger2DeploymentId]?.serialNo),
              },
              raw: correlation,
              selected$: computed(
                () =>
                  this.activeTabS() === LdTabs.Correlations &&
                  !!this.ldCoordinatorStore.selectedCorrelationsMapS()[correlation.id]
              ),
              correlationPath$: computed(() => {
                const relatedCorrelationPaths = this.correlationPathsS().filter(
                  correlationPath => correlationPath.correlation.id === correlation.id
                );

                return relatedCorrelationPaths.length ? relatedCorrelationPaths[0] : null;
              }),
              toastMessage$: computed(() => this.markerToastService.messageFor(correlation.id)),
            };
          })
      )
    );
  }

  public override getCorrelationPaths(): Observable<IMapCorrelationPath[]> {
    return this._correlationPathsView.pipe(
      tap(({ paths, focus }) => this.applyCorrelationPathsFocus(paths, focus)),
      map(({ paths }) => paths)
    );
  }

  private applyCorrelationPathsFocus(paths: IMapCorrelationPath[], focus: ILdCorrelationPathsFocus): void {
    if (!paths.length || focus.type === 'none') return;

    // Logger-driven selection: only pin (center) on the logger, do not change zoom.
    if (focus.type === 'pin') {
      this.pinTrigger(focus.position);
      return;
    }

    // Correlation-driven selection: zoom to fit the correlation paths.
    if (paths.some(path => path.pipes.length)) {
      this.zoomToFitTrigger(ZoomToFitType.CorrelationPaths);
    } else {
      this.zoomToFitTrigger(
        ZoomToFitType.Boundary,
        paths.flatMap(path => [
          ...path.loggers.map(logger => logger.deployment.coordinates),
          path.correlation.coordinates,
        ])
      );
    }
  }

  public override getEventMarkers(): Observable<IEventMarker[]> {
    return combineLatest([
      this.store.select(selectAllEvents),
      this.doFiltersService.getFilter(DoFilterKey.Events),
      this.eventPositionOverride$,
    ]).pipe(
      map(([events, eventFilter, override]) => ({
        events: events.filter(event => isEventVisible(eventFilter, event)),
        override,
      })),
      map(({ events, override }) =>
        events.map(event => {
          return {
            id: event.id,
            type: event.status,
            position: (event.id === override.eventId && override.position) || event.coordinates,
            config: {
              showTooltip: true,
            },
            info: {
              status: event.status,
              investigationResult: deriveLdEventInvestigationResult(event),
              repairStatus: deriveLdEventRepairStatus(event),
              id: event.id,
            },
            raw: event,
            selected$: computed(
              () => this.activeTabS() === LdTabs.Events && !!this.ldCoordinatorStore.selectedEventsMapS()[event.id]
            ),
            // while an event is moved, the others step back
            dimmed$: computed(() => {
              const moved = this.eventPosition.event();

              return !!moved && moved.id !== event.id;
            }),
          };
        })
      )
    );
  }

  public override getPipes(): Observable<IPipePolyline[]> {
    const polylines$ = combineLatest([
      this.mapPipesService.getPipes$(this.store.select(selectPipesSource)),
      this.store.select(selectMainProjectViewMeasurementSystem),
    ]).pipe(map(([pipes, measurementSystem]) => this.toPipePolylines(pipes, measurementSystem)));

    // no build while the filter hides the pipes; turned back on, an unchanged network is served from
    // MapPipesService's last build at once
    return this.pipesVisible$.pipe(
      switchMap(visible => (visible ? polylines$ : of([]))),
      tap(polylines => this.keepSelectedPipesOf(polylines))
    );
  }

  private readonly toPipePolylines = this.mapPipesService.createPolylineMapper(
    (pipe, measurementSystem: MeasurementSystem): IPipePolyline<IMapPipe> => ({
      ...this.mapPipesService.toPolylineBase(pipe),
      selected$: computed(() => !!this.selectedPipesMapS()[pipe.id]),
      info: {
        diameterMm: pipe.config?.diameterMm,
        material: pipe.config?.material,
        length: pipe.length,
        measurementSystem,
      },
      config: {
        clickable: true,
        showTooltip: true,
      },
    })
  );

  private keepSelectedPipesOf(polylines: IPipePolyline[]): void {
    const selected = this._selectedPipes.getValue();
    if (!selected.length) return;

    const ids = new Set(polylines.map(polyline => polyline.id));
    const kept = selected.filter(pipe => ids.has(pipe.id));

    if (kept.length !== selected.length) this.setSelectedPipes(kept);
  }

  public override getMapOverlays(): Observable<IMapOverlay[]> {
    return combineLatest([
      this.store.select(selectAllMapOverlays),
      this.doFiltersService.getFilter(DoFilterKey.PipesNetwork),
    ]).pipe(
      map(([overlays, pipesNetworkFilter]) =>
        overlays.filter(overlay => this.doFiltersService.isMapOverlayVisible(pipesNetworkFilter, overlay.id))
      )
    );
  }

  private getProjectChangedHandler(): Observable<Nullable<string>> {
    return this.store.select(selectMainProjectId).pipe(
      distinctUntilChanged(),
      skip(1),
      tap(() => this.mapOverlayLoaderService.clearCache())
    );
  }

  private getNewDataLoadingHandler(): Observable<boolean> {
    return this.store.select(selectMainDataIsLoading).pipe(
      filter(value => value),
      tap(() => {
        this.ldCoordinatorStore.dispatch({ type: LdActionType.ResetSelection });
        this.resetMapSelection();
        this.correlationPathsService.clearCache();
        // the event being moved may not be part of the new data
        this.eventPosition.exit();
      })
    );
  }

  private getCorrelationPathLoadingHandler(): Observable<boolean> {
    return this.correlationPathsService.loading$.pipe(
      skip(1),
      tap(value => {
        this.mapLoading(value);
      })
    );
  }

  private getToolbarZoomToFitHandler(): Observable<boolean> {
    return this.doToolbarService.zoomToFit.pipe(
      switchMap(value => this.zoomToFitByAreaBoundaries$.pipe(map(() => value)))
    );
  }

  private getCoordinatorActionZoomToFitItemHandler(): Observable<ICorrelation[] | ILdLogger[] | IEvent[]> {
    return this.ldCoordinatorStore.zoomToFitItemAction$.pipe(
      switchMap(({ tab, item }) => {
        switch (tab) {
          case LdTabs.LoggerNoise: {
            return this.handleLoggerSelection([item as ILdLogger]);
          }
          case LdTabs.Correlations: {
            return this.handleCorrelationSelection([item as ICorrelation]);
          }
          case LdTabs.Events: {
            return this.handleEventSelection([item as IEvent]);
          }
          default: {
            return of([]);
          }
        }
      })
    );
  }

  private getCoordinatorActionShowInStreetViewHandler(): Observable<LdCoordinatorActionShowInStreetView> {
    return this.ldCoordinatorStore.showInStreetViewAction$.pipe(
      tap(({ position }) => this.streetViewTrigger(position))
    );
  }

  private getCoordinatorSelectionChangedHandler(): Observable<ICorrelation[] | ILdLogger[] | IEvent[]> {
    return combineLatest([this.ldCoordinatorStore.selectionChanged$, this.ldCoordinatorStore.activeTab$]).pipe(
      auditTime(0),
      switchMap(([selection, activeTab]) => {
        switch (activeTab) {
          case LdTabs.LoggerNoise: {
            return this.handleLoggerSelection(selection.loggers);
          }
          case LdTabs.Correlations: {
            return this.handleCorrelationSelection(selection.correlations);
          }
          case LdTabs.Events: {
            return this.handleEventSelection(selection.events);
          }
          default: {
            this.resetMapSelection();
            return of([]);
          }
        }
      })
    );
  }

  private getLoggerMarkerActionHandler(): Observable<ILoggerMarkerAction> {
    return this.loggerMarkerAction$.pipe(
      tap(action => {
        const item = action.item;

        switch (action.type) {
          case LoggerMarkerAction.Click: {
            if (this.cellIdModeEnabled()) {
              this.ldCoordinatorStore.dispatch({
                type: LdActionType.CellIdLoggerClick,
                place: LdActionPlace.Map,
                item,
              });
            } else {
              this.ldCoordinatorStore.dispatch({
                type: LdActionType.LoggerClick,
                place: LdActionPlace.Map,
                item,
              });
            }
            break;
          }
          case LoggerMarkerAction.CtrlClick: {
            this.ldCoordinatorStore.dispatch({
              type: LdActionType.LoggerCtrlClick,
              place: LdActionPlace.Map,
              item,
            });
            break;
          }
          case LoggerMarkerAction.DbClick: {
            this.ldCoordinatorStore.dispatch({
              type: LdActionType.LoggerDbClick,
              place: LdActionPlace.Map,
              item,
            });
            break;
          }
          case LoggerMarkerAction.Contextmenu: {
            if (!action.event || this.openEventPositionMenu(action.event)) break;

            this.ldCoordinatorStore.dispatch({
              type: LdActionType.LoggerContextMenu,
              place: LdActionPlace.Map,
              item,
              event: action.event,
            });
            break;
          }
        }
      })
    );
  }

  private getMapRightClickHandler(): Observable<IMapRightClick> {
    return this.mapRightClick$.pipe(
      tap(({ position, event }) => {
        if (this.openEventPositionMenu(event)) return;

        this.ldCoordinatorStore.dispatch({
          type: LdActionType.MapAreaContextMenu,
          place: LdActionPlace.Map,
          position,
          event,
        });
      })
    );
  }

  private getMapClickHandler(): Observable<IMapClick> {
    return this.mapClick$.pipe(
      filter(() => this.eventPosition.isActive()),
      tap(({ position }) => this.eventPosition.pick(position))
    );
  }

  private getEventPositionModeHandler(): Observable<IEvent | null> {
    return this.eventPositionEvent$.pipe(
      withLatestFrom(this.mapBounds$.pipe(startWith(null))),
      tap(([event, bounds]) => {
        this.mapCursorTrigger(event ? MAP_POSITION_PICK_CURSOR : null);

        // started from the table, the event may be off screen — the map comes to it
        if (event && (!bounds || !isPointInBounds(bounds, PositionAdapter.toGoogle(event.coordinates)))) {
          this.pinTrigger(event.coordinates);
        }
      }),
      map(([event]) => event)
    );
  }

  /**
   * While an event is being moved, a right click on that event opens the mode's own menu, and anywhere
   * else on the map - nothing.
   */
  private openEventPositionMenu(event: MouseEvent, eventId?: string): boolean {
    const moved = this.eventPosition.event();

    if (!moved) return false;

    this.contextMenuService.open(event, eventId === moved.id ? this.ldContextMenuEventPosition.getMapItems() : []);

    return true;
  }

  private getMapBoundaryActionHandler(): Observable<IMapBounds> {
    return this.mapBounds$.pipe(
      tap(bounds => {
        this.ldCoordinatorStore.dispatch({
          type: LdActionType.ChangedMapBounds,
          bounds,
        });
      })
    );
  }

  private getCorrelationMarkerActionHandler(): Observable<ICorrelationMarkerAction> {
    return this.correlationMarkerAction$.pipe(
      tap(action => {
        const item = action.item;

        switch (action.type) {
          case CorrelationMarkerAction.Click: {
            this.ldCoordinatorStore.dispatch({
              type: LdActionType.CorrelationClick,
              place: LdActionPlace.Map,
              item,
            });
            break;
          }
          case CorrelationMarkerAction.CtrlClick: {
            this.ldCoordinatorStore.dispatch({
              type: LdActionType.CorrelationCtrlClick,
              place: LdActionPlace.Map,
              item,
            });
            break;
          }
          case CorrelationMarkerAction.DbClick: {
            this.ldCoordinatorStore.dispatch({
              type: LdActionType.CorrelationDbClick,
              place: LdActionPlace.Map,
              item,
            });
            break;
          }
          case CorrelationMarkerAction.Contextmenu: {
            if (!action.event || this.openEventPositionMenu(action.event)) break;

            this.ldCoordinatorStore.dispatch({
              type: LdActionType.CorrelationContextMenu,
              place: LdActionPlace.Map,
              item,
              event: action.event,
            });
            break;
          }
        }
      })
    );
  }

  private getCorrelationPathActionHandler(): Observable<IMapCorrelationPathAction> {
    return this.correlationPathAction$.pipe(
      tap(action => {
        if (action.type !== CorrelationPathAction.Click) return;

        const item = action.item;
        const index = action.altCorrelationIndex;
        const selectedCorrelationPathStates = this.selectedCorrelationPathStatesS();

        if (
          item &&
          !selectedCorrelationPathStates.find(
            state => state.correlationPath?.id === item.id && state.altCorrelationIndex === index
          )
        ) {
          this.setSelectedCorrelationPathStates([
            {
              correlationPath: item,
              altCorrelationIndex: index,
            },
          ]);
        } else {
          this.setSelectedCorrelationPathStates([]);
        }
      })
    );
  }

  private getEventMarkerActionHandler(): Observable<IEventMarkerAction> {
    return this.eventMarkerAction$.pipe(
      tap(action => {
        const item = action.item;

        // every marker here is a saved event — only an event not created yet has none
        if (!item) return;

        switch (action.type) {
          case EventMarkerAction.Click: {
            this.ldCoordinatorStore.dispatch({
              type: LdActionType.EventClick,
              place: LdActionPlace.Map,
              item,
            });
            break;
          }
          case EventMarkerAction.DbClick: {
            this.ldCoordinatorStore.dispatch({
              type: LdActionType.EventDbClick,
              place: LdActionPlace.Map,
              item,
            });
            break;
          }
          case EventMarkerAction.Contextmenu: {
            if (!action.event || this.openEventPositionMenu(action.event, item.id)) break;

            this.ldCoordinatorStore.dispatch({
              type: LdActionType.EventContextMenu,
              place: LdActionPlace.Map,
              item,
              event: action.event,
            });
            break;
          }
        }
      })
    );
  }

  private getPipePolylineActionHandler(): Observable<IPipePolylineAction> {
    return this.pipePolylineAction$.pipe(
      tap(action => {
        switch (action.type) {
          case PipePolylineAction.Click: {
            this.pipeClick(action);
            break;
          }
          case PipePolylineAction.Contextmenu: {
            this.pipeContextmenu(action);
            break;
          }
        }
      })
    );
  }

  private pipeClick(action: IPipePolylineAction): void {
    // pipes take the click from the map, while an event is moved onto one it is the new spot
    if (this.eventPosition.isActive()) {
      if (action.position) this.eventPosition.pick(action.position);

      return;
    }

    const item = action.item;
    const selectedPipesMap = this.selectedPipesMapS();

    if (item && !selectedPipesMap[item.id]) {
      this.setSelectedPipes([item]);
    } else {
      this.setSelectedPipes([]);
    }
  }

  // like the other menus, a right click makes the pipe the only selected one and never toggles it off
  private pipeContextmenu({ item, event }: IPipePolylineAction): void {
    if (!item || !event || this.openEventPositionMenu(event)) return;

    this.setSelectedPipes([item]);
    this.ldCoordinatorStore.dispatch({ type: LdActionType.PipeContextMenu, place: LdActionPlace.Map, item, event });
  }

  private resetMapSelection() {
    this.setSelectedPipes([]);
    this.setSelectedCorrelationPathStates([]);
    this.setCorrelationPathsView([]);
  }

  private handleLoggerSelection(selectedLoggers: ILdLogger[]): Observable<ILdLogger[]> {
    return this.ldLoggerFiltersService.getLoggerFilters().pipe(
      withLatestFrom(
        this.ldDataTimedService.correlations$,
        this.ldCoordinatorStore.lastActionPlace$,
        this.ldCoordinatorStore.lastActionType$
      ),
      switchMap(([loggerFilters, correlations, lastActionPlace, lastActionType]) => {
        this.resetMapSelection();

        if (!selectedLoggers.length || selectedLoggers.length !== 1) {
          return of(selectedLoggers);
        }

        const item = selectedLoggers[0];

        if (!isLoggerVisible(loggerFilters, item)) {
          return of(selectedLoggers);
        }

        const isCellIdAction =
          lastActionType === LdActionType.CellIdClick || lastActionType === LdActionType.CellIdLoggerClick;
        const isContextMenuAction = this.isContextMenuAction(lastActionType);

        const relatedCorrelations = correlations.filter(
          correlation =>
            correlation.logger1DeploymentId === item.deployment.id ||
            correlation.logger2DeploymentId === item.deployment.id
        );

        if (relatedCorrelations.length && !isCellIdAction && !isContextMenuAction) {
          return this.correlationPathsService.getCorrelationPathsMap(relatedCorrelations.map(c => c.id)).pipe(
            map(correlationPathsMap => {
              this.setCorrelationPathsView(
                relatedCorrelations
                  .filter(correlation => !!correlationPathsMap[correlation.id])
                  .map(correlation =>
                    this.prepareMapCorrelationPath(
                      correlation,
                      {
                        ...correlationPathsMap[correlation.id],
                        alternativeCorrelations: undefined,
                      },
                      selectedLoggers
                    )
                  ),
                // Loggers only pin (not zoom to fit), even when their correlation paths are drawn.
                { type: 'pin', position: item.deployment.coordinates }
              );

              return selectedLoggers;
            })
          );
        }

        if (
          (lastActionPlace === LdActionPlace.TabsPanel || lastActionPlace === LdActionPlace.Programmatic) &&
          !isCellIdAction &&
          !isContextMenuAction
        ) {
          this.pinTrigger(item.deployment.coordinates);
        }

        return of(selectedLoggers);
      })
    );
  }

  private handleCorrelationSelection(selectedCorrelations: ICorrelation[]): Observable<ICorrelation[]> {
    return this.doFiltersService.getFilter(DoFilterKey.Correlation).pipe(
      withLatestFrom(this.ldDataTimedService.ldLoggersMapByDeploymentId$, this.ldCoordinatorStore.lastActionType$),
      switchMap(([correlationFilter, loggersMapByDeploymentId, lastActionType]) => {
        this.resetMapSelection();

        if (!selectedCorrelations.length || selectedCorrelations.length !== 1) {
          return of(selectedCorrelations);
        }

        const item = selectedCorrelations[0];

        if (!isCorrelationVisible(correlationFilter, item) || this.isContextMenuAction(lastActionType)) {
          return of(selectedCorrelations);
        }

        return this.correlationPathsService.getCorrelationPathsMap([item.id]).pipe(
          map(correlationPathsMap => {
            this.setCorrelationPathsView(
              [item]
                .filter(correlation => !!correlationPathsMap[correlation.id])
                .map(correlation =>
                  this.prepareMapCorrelationPath(
                    correlation,
                    correlationPathsMap[correlation.id],
                    [
                      this.ldDataTimedService.getActualLdLogger(
                        loggersMapByDeploymentId[correlation.logger1DeploymentId]
                      ),
                      this.ldDataTimedService.getActualLdLogger(
                        loggersMapByDeploymentId[correlation.logger2DeploymentId]
                      ),
                    ].filter((logger): logger is ILdLogger => !!logger)
                  )
                )
            );

            return selectedCorrelations;
          })
        );
      })
    );
  }

  private handleEventSelection(selectedEvents: IEvent[]): Observable<IEvent[]> {
    return this.doFiltersService.getFilter(DoFilterKey.Events).pipe(
      withLatestFrom(this.ldCoordinatorStore.lastActionPlace$, this.ldCoordinatorStore.lastActionType$),
      map(([eventFilter, lastActionPlace, lastActionType]) => {
        this.resetMapSelection();

        if (!selectedEvents.length || selectedEvents.length !== 1) {
          return selectedEvents;
        }

        const item = selectedEvents[0];

        const isVisible = isEventVisible(eventFilter, item);

        if (
          item?.coordinates &&
          isVisible &&
          lastActionPlace === LdActionPlace.TabsPanel &&
          !this.isContextMenuAction(lastActionType)
        ) {
          this.zoomToFitTrigger(ZoomToFitType.Boundary, [item.coordinates]);
        }

        return selectedEvents;
      })
    );
  }

  /**
   * A right click selects the item so the menu acts on something highlighted, but it must not
   * move the map — otherwise the map slides out from under the still-open menu.
   */
  private isContextMenuAction(lastActionType: LdActionType | null): boolean {
    return (
      lastActionType === LdActionType.LoggerContextMenu ||
      lastActionType === LdActionType.CorrelationContextMenu ||
      lastActionType === LdActionType.EventContextMenu
    );
  }

  private getCorrelationPathColor(status: CommonStateStatus) {
    return status === CommonStateStatus.Critical
      ? this.mapStylesConfig.mapColor.pipeCorrelationRed
      : status === CommonStateStatus.Warning
        ? this.mapStylesConfig.mapColor.pipeCorrelationYellow
        : undefined;
  }

  private getCellIdColor(id: string | null | undefined): string | undefined {
    if (!id) return undefined;

    return this.colorManagementService.getColorInstanceById(id, ColorGroup.Regular).toRgbString();
  }

  private prepareMapCorrelationPath(
    correlation: ICorrelation,
    correlationPath: ICorrelationPath,
    loggers: ILdLogger[]
  ): IMapCorrelationPath {
    return {
      id: correlation.id,
      correlation: correlation,
      pipes: correlationPath
        ? correlationPath.pipeSegments.map(segment => ({ id: segment.id, paths: [segment.nodes], raw: segment }))
        : [],
      loggers,
      config: {
        strokeColor: this.getCorrelationPathColor(correlation.qualityStatus),
        pipeClickable: false,
      },
      altCorrelations: correlationPath?.alternativeCorrelations?.map(altCorrelation => {
        return {
          position: altCorrelation.coordinates,
          pipes: altCorrelation.pipeSegments.map(segment => ({ id: segment.id, paths: [segment.nodes], raw: segment })),
        };
      }),
      selectedAltCorrelationIndex$: computed(() => {
        const selectedCorrelationPathStates = this.selectedCorrelationPathStatesS();

        const relatedCorrelationPathState = selectedCorrelationPathStates.find(
          state => state.correlationPath && state.correlationPath.id === correlation.id
        );

        return relatedCorrelationPathState?.altCorrelationIndex;
      }),
    };
  }
}
